/**
 * `/admin/support` — Support & retours.
 *
 * Liste tous les retours utilisateurs (bugs, améliorations, questions) et permet
 * de changer leur statut. Source : /api/feedback (backend).
 */
import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { AdminPageHeader, AdminCard } from "./ui";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import StatusBadge from "@/components/common/StatusBadge";
import SmartLoader from "@/components/common/SmartLoader";
import { RefreshCw, MessageCircle } from "lucide-react";
import { toast } from "sonner";

const STATUS_TONES = {
  open: "warning", triaged: "info", in_progress: "info",
  resolved: "success", wont_fix: "neutral",
};
const KIND_LABELS = {
  bug: "Bug", improvement: "Amélioration",
  question: "Question", other: "Autre",
};
const STATUS_OPTIONS = [
  { value: "open", label: "Ouvert" },
  { value: "triaged", label: "Trié" },
  { value: "in_progress", label: "En cours" },
  { value: "resolved", label: "Résolu" },
  { value: "wont_fix", label: "Rejeté" },
];

export default function AdminSupportPage() {
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState("all");
  const [severity, setSeverity] = useState("all");
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  const load = async () => {
    setLoading(true); setErr(null);
    try {
      const params = { limit: 200 };
      if (status !== "all") params.status = status;
      if (severity !== "all") params.severity = severity;
      const { data } = await api.get("/feedback", { params });
      setItems(data.items || []);
    } catch (e) {
      setErr(e.response?.data?.detail || "Impossible de charger les retours");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status, severity]);

  const setFeedbackStatus = async (id, newStatus) => {
    try {
      await api.patch(`/feedback/${id}`, { status: newStatus });
      toast.success("Statut mis à jour");
      setItems((old) => old.map((i) => i.id === id ? { ...i, status: newStatus } : i));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Impossible de mettre à jour");
    }
  };

  const counts = useMemo(() => {
    const c = { open: 0, in_progress: 0, resolved: 0, total: items.length };
    items.forEach((i) => { if (c[i.status] != null) c[i.status]++; });
    return c;
  }, [items]);

  return (
    <div className="animate-fade-up">
      <AdminPageHeader
        overline="Support"
        title="Retours utilisateurs & tickets"
        description="Bugs, améliorations et questions envoyés depuis le widget « Feedback » de l'app. Changez le statut au fur et à mesure du traitement."
        testId="admin-support-header"
        actions={
          <Button onClick={load} className="bg-accent text-accent-foreground hover:bg-accent/90"
                  data-testid="admin-support-refresh">
            <RefreshCw className="h-3.5 w-3.5 mr-1.5" /> Actualiser
          </Button>
        }
      />

      <div className="grid gap-3 grid-cols-2 lg:grid-cols-4 mb-6">
        {[
          { label: "Total", value: counts.total },
          { label: "Ouverts", value: counts.open },
          { label: "En cours", value: counts.in_progress },
          { label: "Résolus", value: counts.resolved },
        ].map((k) => (
          <AdminCard key={k.label} className="p-4">
            <div className="text-[10px] uppercase tracking-widest text-white/50">{k.label}</div>
            <div className="mt-1 font-mono text-2xl">{k.value}</div>
          </AdminCard>
        ))}
      </div>

      <AdminCard className="p-4 mb-4 flex flex-wrap gap-3 items-end">
        <div className="w-40">
          <div className="text-xs text-white/60 mb-1">Statut</div>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger data-testid="admin-support-filter-status"
                           className="bg-white/5 border-white/10 text-white">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous</SelectItem>
              {STATUS_OPTIONS.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="w-40">
          <div className="text-xs text-white/60 mb-1">Gravité</div>
          <Select value={severity} onValueChange={setSeverity}>
            <SelectTrigger data-testid="admin-support-filter-severity"
                           className="bg-white/5 border-white/10 text-white">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Toutes</SelectItem>
              <SelectItem value="low">Faible</SelectItem>
              <SelectItem value="medium">Moyenne</SelectItem>
              <SelectItem value="high">Élevée</SelectItem>
              <SelectItem value="critical">Critique</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </AdminCard>

      {loading || err ? (
        <SmartLoader label="Chargement des retours…" onRetry={load} error={err} />
      ) : items.length === 0 ? (
        <AdminCard className="p-12 text-center text-white/60">
          <MessageCircle className="h-10 w-10 mx-auto mb-3 text-white/30" />
          Aucun retour utilisateur pour l&apos;instant.
        </AdminCard>
      ) : (
        <AdminCard className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-white/5 text-xs uppercase text-white/50">
              <tr className="text-left">
                <th className="px-3 py-3">Titre</th>
                <th className="px-3 py-3">Type</th>
                <th className="px-3 py-3">Gravité</th>
                <th className="px-3 py-3">Utilisateur</th>
                <th className="px-3 py-3">Reçu le</th>
                <th className="px-3 py-3">Statut</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/10">
              {items.map((i) => (
                <tr key={i.id} className="hover:bg-white/5" data-testid={`admin-support-row-${i.id}`}>
                  <td className="px-3 py-3">
                    <div className="font-medium text-white">{i.title}</div>
                    <div className="text-xs text-white/50 max-w-md truncate" title={i.description}>{i.description}</div>
                  </td>
                  <td className="px-3 py-3 text-xs text-white/80">{KIND_LABELS[i.kind] || i.kind}</td>
                  <td className="px-3 py-3">
                    <StatusBadge tone={i.severity === "critical" ? "danger" : i.severity === "high" ? "warning" : "neutral"}>
                      {i.severity}
                    </StatusBadge>
                  </td>
                  <td className="px-3 py-3 text-xs font-mono text-white/70">{i.user_email}</td>
                  <td className="px-3 py-3 text-xs text-white/60">{(i.created_at || "").slice(0, 16).replace("T", " ")}</td>
                  <td className="px-3 py-3">
                    <Select value={i.status} onValueChange={(v) => setFeedbackStatus(i.id, v)}>
                      <SelectTrigger className="w-36 h-8 bg-white/5 border-white/10 text-white"
                                     data-testid={`admin-support-status-${i.id}`}>
                        <SelectValue>
                          <StatusBadge tone={STATUS_TONES[i.status] || "neutral"}>{i.status}</StatusBadge>
                        </SelectValue>
                      </SelectTrigger>
                      <SelectContent>
                        {STATUS_OPTIONS.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </AdminCard>
      )}
    </div>
  );
}
